/**
 * @fileoverview System Admin Modules catalog management page.
 * @module system-web/pages/ModulesPage
 *
 * Responsibilities:
 * - Render the platform module catalog from Dexie via `moduleService`.
 * - Support creating, editing, soft-deleting, and JSON seed import/export of modules.
 *
 * Dependencies:
 * - react, lucide-react, @firesquirrel/local-db, ../services/moduleService, @ui-admin/contexts/ThemeContext.
 *
 * Architecture:
 * - Local-first page reading/writing through `moduleService` (Dexie `modules` table + background sync).
 *
 * Notes:
 * - Module `code` is mechanically derived from `name` on creation and immutable thereafter.
 */
import React, { useEffect, useState } from 'react';
import { Package, Pencil, Plus, Trash2, X } from 'lucide-react';
import { generateObjectId } from '@firesquirrel/local-db';
import ConfirmActionModal from '../components/ui/ConfirmActionModal';
import { deleteModule, formatModuleSeedJson, generateModuleCode, listModules, seedModulesFromJson, upsertModule } from '../services/moduleService';
import { getWebConfig } from '../config';
import { useTheme } from '@ui-admin/contexts/ThemeContext';

const STATUS_COLORS: Record<string, string> = {
  ga: 'bg-green-100 text-green-700',
  beta: 'bg-yellow-100 text-yellow-700',
  alpha: 'bg-blue-100 text-blue-700',
  deprecated: 'bg-red-100 text-red-700',
};

export function createModuleDraft() {
  return {
    _id: generateObjectId(),
    code: '',
    name: '',
    description: '',
    status: 'alpha',
    enabledByDefault: false,
  };
}

export default function ModulesPage() {
  const { theme } = useTheme();
  const { appEnv } = getWebConfig();
  const [modules, setModules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null);
  const [editingIsNew, setEditingIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedJsonDraft, setSeedJsonDraft] = useState('');

  const load = () => {
    listModules()
      .then((items) => setModules(items.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '') || (a.code ?? '').localeCompare(b.code ?? ''))))
      .catch(() => setModules([]))
      .finally(() => setLoading(false));
  };

  const handleConfirmSeed = async () => {
    setSeeding(true);
    try {
      await seedModulesFromJson(seedJsonDraft);
      await Promise.resolve(load());
      setSeedConfirmOpen(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to seed modules');
    } finally {
      setSeeding(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteModule(deleteTarget._id);
      setModules((items) => items.filter((m) => m._id !== deleteTarget._id));
      if (editing?._id === deleteTarget._id) closeEditor();
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete module');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(load, []);

  const startCreate = () => {
    setError(null);
    setEditing(createModuleDraft());
    setEditingIsNew(true);
  };

  const startEdit = (module: any) => {
    setError(null);
    setEditing({ ...module });
    setEditingIsNew(false);
  };

  const startDelete = (module: any) => {
    setError(null);
    setDeleteTarget(module);
  };

  const closeEditor = () => {
    setEditing(null);
    setEditingIsNew(false);
  };

  const handleSave = async () => {
    if (!editing) return;
    const code = isCreateMode ? generatedCode : String(editing.code ?? '').trim();
    if (!code || !editing.name.trim()) {
      setError('Code and name are required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const next = {
        ...editing,
        code,
        name: editing.name.trim(),
        description: editing.description?.trim() || undefined,
        status: editing.status,
        enabledByDefault: !!editing.enabledByDefault,
      };

      await upsertModule(next);
      closeEditor();
      await Promise.resolve(load());
    } catch (err: any) {
      setError(err?.message || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const statuses = ['alpha', 'beta', 'ga', 'deprecated'] as const;
  const statusDescriptions: Record<string, string> = {
    alpha: 'Experimental or newly introduced module.',
    beta: 'Usable, but still being validated.',
    ga: 'Stable and generally available.',
    deprecated: 'No longer recommended for new use.',
  };

  const isCreateMode = editingIsNew;
  const generatedCode = isCreateMode ? generateModuleCode(editing?.name) : String(editing?.code ?? '');
  const reseedButtonLabel = appEnv === 'dev' ? 'Update seeding JSON' : 'Manually Update Modules';
  const reseedModalTitle = appEnv === 'dev' ? 'Update seeding JSON' : 'Manually Update Modules';
  const reseedModalDescription = appEnv === 'dev'
    ? 'Review the JSON below. It was generated from the current module list in this environment. When you confirm, this JSON will be sent as the manual reseed payload for the next environment.'
    : 'Review the JSON below. It was generated from the current module list in this environment. When you confirm, this JSON will be sent to manually update the module seed payload for this environment.';
  const reseedModalWarning = appEnv === 'dev'
    ? 'Startup no longer reseeds modules automatically. This button only updates the seed JSON for manual reseeding.'
    : 'Startup no longer reseeds modules automatically. This button manually updates the modules seed payload.';

  return (
    <div className="theme-grid p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modules</h1>
          <p className="text-gray-500 mt-1">Platform module catalog and availability</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSeedJsonDraft(formatModuleSeedJson(modules));
              setSeedConfirmOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-orange-300 text-orange-700 text-sm font-medium hover:bg-orange-50 transition-colors"
          >
            {reseedButtonLabel}
          </button>
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors"
          >
            <Plus className="h-4 w-4" /> New Module
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss module error" className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : modules.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No modules found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => (
            <div key={m._id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{m.name}</h3>
                  <p className="text-xs text-gray-400 font-mono">{m.code}</p>
                </div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[m.status] || 'bg-gray-100 text-gray-600'}`}>{m.status}</span>
              </div>
              {m.description && <p className="text-sm text-gray-500 mb-3">{m.description}</p>}
              <div className="flex items-center justify-between text-xs text-gray-400">
                {m.enabledByDefault && <span className="text-green-600 font-medium">Default ON</span>}
              </div>
              <div className="mt-4 flex justify-end">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => startEdit(m)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => startDelete(m)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ background: theme.cardBg }}>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isCreateMode ? 'Create Module' : 'Edit Module'}
                </h2>
                <p className="text-xs text-gray-500">System-wide module catalog entry</p>
              </div>
              <button type="button" onClick={closeEditor} aria-label="Close module editor" className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <label className="col-span-2">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Name *</span>
                  <input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Example: Wildland Response"
                  />
                </label>
                <label className="col-span-2">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Code</span>
                  <input
                    value={generatedCode}
                    readOnly
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-gray-50 text-gray-600"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Code is generated automatically from the name. New modules use this value when saved.
                  </p>
                </label>
                <label className="col-span-2">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Description</span>
                  <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </label>
                <label>
                  <span className="block text-xs font-medium text-gray-600 mb-1">Status</span>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm bg-white">
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{statusDescriptions[editing.status] ?? ''}</p>
                </label>
                <label className="col-span-2 flex items-center gap-2 pt-2">
                  <input type="checkbox" checked={!!editing.enabledByDefault} onChange={(e) => setEditing({ ...editing, enabledByDefault: e.target.checked })} />
                  <span className="text-sm text-gray-700">Enabled by default for new tenants</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button type="button" onClick={closeEditor} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : isCreateMode ? 'Create Module' : 'Save Module'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmActionModal
          title="Delete Module"
          description={`Delete ${deleteTarget.name || deleteTarget.code}? This removes the module from the active catalog and marks it deleted.`}
          warning="This is a soft delete. The module is tombstoned so sync and history can preserve the record."
          confirmButtonLabel="Delete Module"
          variant="danger"
          isSubmitting={deleting}
          onConfirm={handleConfirmDelete}
          onClose={() => { if (!deleting) setDeleteTarget(null); }}
        />
      )}

      {seedConfirmOpen && (
        <ConfirmActionModal
          title={reseedModalTitle}
          description={reseedModalDescription}
          warning={reseedModalWarning}
          confirmButtonLabel="Send JSON"
          variant="warning"
          isSubmitting={seeding}
          onConfirm={handleConfirmSeed}
          onClose={() => { if (!seeding) setSeedConfirmOpen(false); }}
        >
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-600">Seed JSON</label>
            <textarea
              value={seedJsonDraft}
              onChange={(e) => setSeedJsonDraft(e.target.value)}
              rows={16}
              className="w-full px-3 py-2 border rounded-lg text-sm font-mono bg-white"
              spellCheck={false}
            />
            <p className="text-xs text-gray-500">
              This JSON is generated from the current module list. Edit it before seeding if you want to add, remove, or rename modules.
            </p>
          </div>
        </ConfirmActionModal>
      )}
    </div>
  );
}
